import { DataSource, Figure } from "@hydrologiq/schemas/1.0.0/installationPage"
import IBuilder from "../IBuilder"
import { IFiguresProps } from "../IFigures"
import UsageFigure from "./figures/UsageFigure"
import FigureGrid from "@hydrologiq/shared/figures/generic/FigureGrid"
import LatestUsageFigure from "./figures/LatestUsageFigure"
import ForecastedHydrogenFigure from "./figures/ForecastedHydrogen"
import WeightUsageFigure from "./figures/WeightUsageFigure"
import HydrogenFuelLevelFigure from "./figures/HydrogenFuelLevelFigure"
import InstallationEmissionsFactor from "./figures/InstallationEmissionsFactor"
import { JSX } from "react"
import InstallationEmissions from "./figures/InstallationEmissions"
import GenericComparison from "./figures/GenericComparison"
import Placeholder from "../../figures/Placeholder"
import MonitoringStatus from "@hydrologiq/shared/figures/asset/MonitoringStatus"
import { Asset } from "@hydrologiq/gql-experience/graphql"
import { AssetMonitoring } from "@hydrologiq/gql/graphql"

type FiguresProps = IFiguresProps<Figure> & {
  context?: Asset
  monitoring?: AssetMonitoring
}

const Figures: IBuilder<FiguresProps> = (props: FiguresProps) => {
  const maxFigures = 6
  const elements: JSX.Element[] = []
  const createFigure = (figure: Figure) => {
    if (elements.length > maxFigures) {
      console.error("No more room to create figure")
      return
    }

    let element: React.ReactNode = undefined
    const usage = figure.usage
    switch (figure.type) {
      case "WeightDifference":
        if (usage)
          element = (
            <WeightUsageFigure
              key={`figures-${elements.length}`}
              title={figure.title}
              installationId={props.installationId}
              usage={usage}
              installationEndDate={props.installationEndDate}
              directComparison={true}
            />
          )
        break
      case "Difference":
      case "Usage":
        if (usage)
          element = (
            <UsageFigure
              key={`figures-${elements.length}`}
              title={figure.title}
              installationId={props.installationId}
              usage={usage}
              installationEndDate={props.installationEndDate}
              directComparison={true}
            />
          )
        break
      case "LatestUsage":
        const latestUsage = figure.latestUsage
        if (latestUsage)
          element = (
            <LatestUsageFigure
              key={`figures-${elements.length}`}
              title={figure.title}
              installationId={props.installationId}
              usage={latestUsage}
              installationEndDate={props.installationEndDate}
            />
          )
        break
      case "HydrogenFuelForecasting":
        const forecast = figure.forecasting
        if (forecast)
          element = (
            <ForecastedHydrogenFigure
              key={`figures-${elements.length}`}
              title={figure.title}
              installationId={props.installationId}
              installationEndDate={props.installationEndDate}
              installationStartDate={props.installationStartDate}
              window={forecast.window}
              threshold={forecast.threshold}
              minPressure={props.minPressure}
              debug={props.ops}
              weighted={true}
            />
          )
        break
      case "HydrogenFuelLevel":
        const fuelLevel = figure.fuelLevel
        if (fuelLevel)
          element = (
            <HydrogenFuelLevelFigure
              key={`figures-${elements.length}`}
              title={figure.title}
              installationId={props.installationId}
              installationStartDate={props.installationStartDate}
              installationEndDate={props.installationEndDate}
              installationMinPressure={props.minPressure}
            />
          )
        break
      case "EmissionsFactor":
        element = (
          <InstallationEmissionsFactor
            key={`figures-${elements.length}`}
            installationId={props.installationId}
            assetId={props.assetId}
            installationEndDate={props.installationEndDate}
            title={figure.title}
          />
        )
        break
      case "Emissions":
        element = (
          <InstallationEmissions
            key={`figures-${elements.length}`}
            installationId={props.installationId}
            assetId={props.assetId}
            installationEndDate={props.installationEndDate}
            title={figure.title}
          />
        )
        break
      case "Comparison":
        element = figure.comparison ? (
          <GenericComparison
            key={`figures-${elements.length}`}
            title={figure.title}
            installationId={props.installationId}
            installationStartDate={props.installationStartDate}
            installationEndDate={props.installationEndDate}
            source={figure.comparison.source as DataSource}
            sourceComparison={figure.comparison.sourceComparison as DataSource}
            unit={figure.comparison.unit}
            description={figure.comparison.description}
            period={figure.comparison.period}
          />
        ) : null
        break
      case "Placeholder":
        element = (
          <Placeholder
            key={`figures-${elements.length}`}
            title={figure.title}
            description={figure.placeholder?.description ?? ""}
            period={figure.placeholder?.period ?? ""}
            subText={figure.placeholder?.subText ?? ""}
          />
        )
        break
      case "MonitoringStatus":
        element =
          props.context && props.monitoring ? (
            <MonitoringStatus
              key={`figures-${elements.length}`}
              context={props.context}
              monitoring={props.monitoring}
            />
          ) : null
        break
    }
    if (element) elements.push(element)
  }
  props.figures.forEach((figures) => createFigure(figures))
  return elements.length > 0 ? <FigureGrid>{elements.map((element) => element)}</FigureGrid> : <></>
}

export default Figures
